import { useCallback, useEffect, useState } from 'react';
import { useScrollProgress } from './motion/useScrollProgress.js';
import { useSmoothScroll } from './lib/useSmoothScroll.js';
import { captureMarketingAttribution } from './lib/marketingAttribution.js';
import { rememberLocalePreference } from './lib/localePreference.js';
import { HomePage } from './routes/HomePage.js';
import { legalRouteFromPath } from './routes/LegalPage.js';
import { PublicLegalPage } from './routes/PublicLegalPage.js';
import { SurveyPage } from './routes/SurveyPage.js';
import { PricingPage } from './routes/PricingPage.js';
import { HowItWorksPage } from './routes/HowItWorksPage.js';
import { WorkerPage } from './routes/WorkerPage.js';
import { AboutPage } from './routes/AboutPage.js';
import { FaqPage } from './routes/FaqPage.js';
import { ZonesPage } from './routes/ZonesPage.js';
import { ContactPage } from './routes/ContactPage.js';
import { ResearchPage, researchPageFromPath } from './routes/ResearchPage.js';
import { SeoIntentPage, seoIntentKeyFromPath } from './routes/SeoIntentPage.js';
import { SeoExpansionPage, seoExpansionKeyFromPath } from './routes/SeoExpansionPage.js';
import { SeoAudiencePage, seoAudienceKeyFromPath } from './routes/SeoAudiencePage.js';
import { ReferralLandingPage, referralCodeFromPath } from './routes/ReferralLandingPage.js';
import { SponsorPayPage, sponsorTokenFromPath } from './routes/SponsorPayPage.js';
import type { WashedLocale } from '@washed/i18n';

export function App({ path: pathProp }: { path?: string } = {}) {
  const [supportsScrollTimeline, setSupportsScrollTimeline] = useState(false);
  useEffect(() => {
    captureMarketingAttribution();
    setSupportsScrollTimeline(
      typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('animation-timeline: scroll()'),
    );
  }, []);
  const pct = useScrollProgress(!supportsScrollTimeline);
  useSmoothScroll();
  const path = pathProp ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const isEnglish = path === '/en' || path.startsWith('/en/');
  const routePath = isEnglish ? path.slice(3) || '/' : path;
  const [locale, setLocale] = useState<WashedLocale>(isEnglish ? 'en' : 'fr');
  const handleLocaleChange = useCallback((nextLocale: WashedLocale) => {
    rememberLocalePreference(nextLocale);
    setLocale(nextLocale);
  }, []);

  const legalRoute = legalRouteFromPath(routePath);
  if (legalRoute !== null) {
    return <PublicLegalPage route={legalRoute} />;
  }
  const sponsorToken = sponsorTokenFromPath(routePath);
  if (sponsorToken !== undefined) {
    return <SponsorPayPage token={sponsorToken} initialLocale={locale} />;
  }
  const referralCode = referralCodeFromPath(routePath);
  if (referralCode !== undefined) {
    return <ReferralLandingPage code={referralCode} />;
  }
  if (
    routePath === '/sondage' ||
    routePath === '/sondage/' ||
    routePath === '/poll' ||
    routePath === '/poll/'
  ) {
    return <SurveyPage locale={locale} onLocaleChange={handleLocaleChange} />;
  }
  if (researchPageFromPath(routePath)) {
    return <ResearchPage locale={locale} onLocaleChange={handleLocaleChange} />;
  }
  const seoIntent = seoIntentKeyFromPath(routePath);
  if (seoIntent !== null) {
    return <SeoIntentPage intent={seoIntent} locale={locale} onLocaleChange={handleLocaleChange} />;
  }
  const seoExpansion = seoExpansionKeyFromPath(routePath);
  if (seoExpansion !== null) {
    return (
      <SeoExpansionPage intent={seoExpansion} locale={locale} onLocaleChange={handleLocaleChange} />
    );
  }
  const seoAudience = seoAudienceKeyFromPath(routePath);
  if (seoAudience !== null) {
    return (
      <SeoAudiencePage intent={seoAudience} locale={locale} onLocaleChange={handleLocaleChange} />
    );
  }
  switch (routePath.replace(/\/+$/u, '') || '/') {
    case '/tarifs':
      return <PricingPage locale={locale} onLocaleChange={handleLocaleChange} />;
    case '/comment-ca-marche':
      return <HowItWorksPage locale={locale} onLocaleChange={handleLocaleChange} />;
    case '/devenir-laveuse':
      return <WorkerPage locale={locale} onLocaleChange={handleLocaleChange} />;
    case '/a-propos':
      return <AboutPage locale={locale} onLocaleChange={handleLocaleChange} />;
    case '/faq':
      return <FaqPage locale={locale} onLocaleChange={handleLocaleChange} />;
    case '/zones':
      return <ZonesPage locale={locale} onLocaleChange={handleLocaleChange} />;
    case '/contact':
      return <ContactPage locale={locale} onLocaleChange={handleLocaleChange} />;
  }
  return (
    <>
      <div
        className="scroll-progress"
        style={supportsScrollTimeline ? undefined : { transform: `scaleX(${pct})` }}
        aria-hidden="true"
      />
      <HomePage locale={locale} onLocaleChange={handleLocaleChange} />
    </>
  );
}
